/* global Vue, LOYALTY_API, LOYALTY_LEVELS, LOYALTY_AWARDS */

import Modal from '../../apps/components/Modal'
import CldImg from '../../apps/components/CldImg'
import { setErrorAndStopLoading, startLoading, stopLoading } from '../../apps/components/Notifications'
import Reservations from '../components/Reservations'
import LevelsProgress from '../components/LevelsProgress'
import MemberCard from '../components/MemberCard'
import SectionHeading from '../components/SectionHeading'
import { AuthService, fetchWithTokenAsync } from '../../AuthService'
import AwardCardList from '../components/AwardCardList'

export default {
  name: 'Dashboard',
  components: { Modal, Reservations, LevelsProgress, SectionHeading, MemberCard, AwardCardList, CldImg },
  template: `
<div class="hero">
  <div class="swiper">
    <div class="swiper-wrapper">
      <div class="swiper-slide">
        <CldImg src="https://res.cloudinary.com/maistra/image/upload/v1637157010/Loyalty/loyalty-dashboard-hero.jpg" alt="" class="hero__poster" width="1440" height="900" />

        <div class="container grid">
          <div class="hero__content col-10">
            <p class="hero__supertitle">{{ $t('dashboard.welcome') }}</p>
            <h1 class="hero-title" data-sl="mask">{{ dashboard.displayName }}</h1>
            <hr>

            <div class="hero__description">
              <p class="h4 no-margin">
                <span v-if="memberLevelName">{{ $t('dashboard.level')}}: {{ memberLevelName }}</span>
                <span>{{ $t('dashboard.membershipNumber') }}: {{ dashboard.loyaltyId }}</span>
              </p>
            </div>

            <p class="no-margin-bottom"><router-link to="/profile">{{ $t('dashboard.editprofile') }}</router-link></p>
          </div>
        </div>
      </div>
    </div>

    <div class="scroll-down scroll-down--hero">
      <i class="icon-arrow-down"></i>
      <span>{{ $t('dashboard.scrollDown') }}</span>
    </div>
  </div>
</div>

<div class="container">
  <MemberCard
    :loyaltyId="dashboard.loyaltyId"
    :memberName="dashboard.displayName"
    :memberSince="dashboard.memberSince"
    :qrCode="dashboard.qrCode"
    @createApplePass="createApplePass"
    @createGooglePass="createGooglePass"
    @enlargeQr="isModalOpen = true" />
</div>

<Modal :isOpen="isModalOpen" @closeModal="isModalOpen = false" :title="dashboard.displayName" class="qr-modal">
  <p class="no-margin">{{ $t('dashboard.level') }}: {{ memberLevelName }}</p>
  <p class="no-margin">{{ $t('dashboard.membershipNumber') }}: {{ dashboard.loyaltyId }}</p>
  <img :src="dashboard.qrCode" width="400" data-sl="mask" />
</Modal>

<div v-if="dashboard.availablePoints >= 0" class="container">
  <div class="grid">
    <div class="col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
      <LevelsProgress
        :availablePoints="dashboard.availablePoints"
        :expiresOn="dashboard.expiresOn"
        :level="dashboard.level"
        :proceed="userLoyaltyLevel?.proceed" />
    </div>
  </div>
</div>

<div class="bookings">
  <hr>
  <div class="container">
    <SectionHeading
      :title="$t('dashboard.myBookings')"
      :button="{href: '/reservations/past', text: $t('dashboard.viewBookings')}"
      rightClass="col-xl-4"
    />
    <div class="grid">
      <div class="col-12 col-lg-10 offset-lg-1 col-xxl-8 offset-xxl-2">
        <Reservations
          :active="true"
          :limit="parseInt(4)"
          :loyaltyId="loyaltyId"
          :properties="properties"
          @error="e => $emit('error', e)" />
      </div>
    </div>
  </div>
</div>
<div class="container section-margin-top loyalty-rewards">
  <SectionHeading
    :title="$t('dashboard.maistarRewards')"
    :button="{href: '/awards', text: $t('dashboard.viewRewards')}"
    rightClass="col-xl-4"
  />
  <AwardCardList v-if="awards.length" :awards="awards.slice(0, 3)" />
</div>
  `,
  emits: ['error'],
  props: {
    loyaltyId: Number,
    properties: Array,
  },
  setup (props, { emit }) {
    const dashboard = Vue.reactive({
      displayName: '',
      level: 0,
      loyaltyId: 0,
      memberSince: null,
      qrCode: '',
      points: 0,
    })

    const awards = Vue.ref([])
    const isModalOpen = Vue.ref(false)
    const { loyaltyId } = Vue.toRefs(props)

    const userLoyaltyLevel = Vue.computed(() => LOYALTY_LEVELS.find(level => level.level === dashboard.level))

    const memberLevelName = Vue.computed(() => {
      return (LOYALTY_LEVELS.find(l => l.level === dashboard.level))?.title
    })

    function download (filename, data) {
      const element = document.createElement('a')
      const parent = document.querySelector('.loyalty-member-card')
      element.setAttribute('href', 'data:application/vnd.apple.pkpass;base64,' + data)
      element.setAttribute('download', filename)
      element.innerText = 'Download Maistra.pkpass'
      parent.appendChild(element)
      element.style.display = 'none'
      element.click()
      parent.removeChild(element)
    }

    async function createApplePass () {
      startLoading('createApplePass')
      try {
        const response = await fetch('/.netlify/functions/create-apple-pass', {
          method: 'POST',
          body: JSON.stringify({ ...dashboard, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const data = await response.text()
        download('Maistra.pkpass', data)
        stopLoading('createApplePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    async function createGooglePass () {
      startLoading('createGooglePass')
      try {
        const response = await fetch('/.netlify/functions/create-google-pass', {
          method: 'POST',
          body: JSON.stringify({ ...dashboard, levelName: memberLevelName.value }),
        })
        if (!response.ok) {
          throw new Error(response.statusText)
        }
        const url = await response.text()
        const link = document.createElement('a')
        link.href = url
        link.target = '_blank'
        link.click()
        stopLoading('createGooglePass')
      } catch (err) {
        setErrorAndStopLoading(err, true)
      }
    }

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('awards-watch')
        const responseAwards = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/getawards/1/1`, AuthService.accessToken, 'GET')
        if (!responseAwards?.awards) {
          throw Error('awards')
        }
        const beternaAwards = responseAwards.awards
        awards.value = beternaAwards.filter(ba => {
          return LOYALTY_AWARDS.some(ca => {
            if (ca.beternaid === ba.prizeID.toString()) {
              Object.assign(ba, ca)
              return true
            }
            return false
          })
        })
        stopLoading('awards-watch')
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    }, { immediate: true })

    Vue.watch(loyaltyId, async () => {
      try {
        startLoading('dashboard-watch')
        dashboard.loyaltyId = props.loyaltyId
        dashboard.displayName = AuthService.getDisplayName()

        const responseQrCode = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/loyaltyprofiles/MyLoyaltyQrCode`, AuthService.accessToken, 'GET')
        if (!responseQrCode?.value || responseQrCode.value.length < 5) {
          throw Error('MyLoyaltyQrCode')
        }
        dashboard.qrCode = `data:image/jpeg;base64,${responseQrCode.value}`

        const responseLoyaltyStatus = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/MyLoyaltyStatus`, AuthService.accessToken, 'GET')
        if (!responseLoyaltyStatus?.loyaltyProfile) {
          throw Error('MyLoyaltyStatus')
        }
        dashboard.memberSince = new Date(responseLoyaltyStatus.loyaltyProfile.createdOn)
        dashboard.level = responseLoyaltyStatus.loyaltyProfile.levelId
        dashboard.availablePoints = responseLoyaltyStatus.loyaltyProfile.availablePoints
        dashboard.expiresOn = new Date(responseLoyaltyStatus.loyaltyProfile.expiresOn)
        const responseAwards = await fetchWithTokenAsync(`${LOYALTY_API}/api/v1/getawards/1/1`, AuthService.accessToken, 'GET')
        if (!responseAwards?.awards) throw Error('awards')
        stopLoading('dashboard-watch')
      } catch (e) {
        setErrorAndStopLoading(e)
      }
    }, { immediate: true })

    return {
      dashboard,
      createApplePass,
      createGooglePass,
      awards,
      isModalOpen,
      memberLevelName,
      LOYALTY_LEVELS,
      userLoyaltyLevel,
    }
  },
}
